/**
 * `<PageShell>` — the single wrapper every workspace page renders into.
 *
 * Renders the experiment card header row — the page heading in the
 * experiment serif on the left (falling back to the active nav-section
 * label from `WorkspaceShell`), description under it, primary actions
 * (search, buttons) on the right — followed by the padded page body.
 */

import { cn } from "@cometchat-ai/ui-dashboard";
import type { ReactNode } from "react";

import { useSectionTitle } from "../layouts/section-title";

interface Props {
  /** Explicit page heading. Defaults to the active nav-section label. */
  title?: ReactNode;
  /** One-line context shown under the title / on the left. */
  description?: ReactNode;
  /** Right-aligned actions: search field, primary buttons, etc. */
  actions?: ReactNode;
  /** Extra classes on the page body (e.g. `max-w-xl` for form pages). */
  className?: string;
  "data-testid"?: string;
  children: ReactNode;
}

export function PageShell({ title, actions, className, children, ...rest }: Props) {
  const sectionTitle = useSectionTitle();
  const heading = title ?? (sectionTitle || null);
  const hasHeader = heading || actions;

  // flex-1 stretches to fill the card on short pages; the implicit
  // min-height:auto of a column flex item lets content grow the card on
  // long pages so the shell's <main> scrolls (min-h-full would cap it).
  return (
    <section className="flex min-h-0 flex-1 flex-col" data-testid={rest["data-testid"]}>
      {hasHeader ? (
        <div
          className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border px-[18px]"
          data-testid="page-shell-header"
        >
          {/* Storybook's content header is a compact single-row h-12 (title +
              actions only — no description), so it stays tight across tabs. */}
          {heading ? (
            <h1 className="min-w-0 truncate text-base font-semibold tracking-[0.01em] text-foreground">
              {heading}
            </h1>
          ) : (
            <span aria-hidden="true" />
          )}
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn("flex-1 p-4", className)}>{children}</div>
    </section>
  );
}
